import type {
  TableRowData,
  UseDataTableQueryOptions,
  PaginationResultsMeta,
  ResponseMeta,
  DataFetchParams,
} from '../types';
import { useEffect, useMemo, useRef } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { DEFAULT_PAGINATION_META, DEFAULT_RESPONSE_META } from '../constants';

/**
 * Compute a stable string fingerprint for the params that affect the total
 * count (filters + search + any extra count-affecting scope values).
 * Sort and pagination do NOT affect count.
 */
function countFingerprint(
  filters: DataFetchParams['filters'],
  search: string | undefined,
  scope?: Record<string, unknown>
): string {
  return JSON.stringify({
    filters,
    search: search ?? '',
    scope: scope ?? null,
  });
}

export function useDataTableQuery<
  TRow extends TableRowData,
  TMeta extends ResponseMeta = ResponseMeta,
>({
  getQueryKeysFromParams,
  queryFn,
  queryConfig,
  onMeta,
  pagination,
  filters,
  sort,
  search,
  countScope,
}: UseDataTableQueryOptions<TRow, TMeta>): UseDataTableQueryResult<TRow> {
  // Track the last known total and the fingerprint of the params that produced it.
  // When only sort/page changes, we pass the previous total so the backend
  // can skip the expensive COUNT query. We also track the queryKey that produced
  // the total so that background refetches/polls of the SAME page recompute the
  // count (rows may have changed) instead of echoing a stale total.
  const lastCountRef = useRef<{
    fingerprint: string;
    total: number;
    queryKeyJson: string;
  } | null>(null);

  const baseParams = useMemo(
    (): DataFetchParams => ({
      pagination,
      filters,
      sort,
      search,
    }),
    [pagination, filters, sort, search]
  );

  const queryKey = useMemo(() => {
    return getQueryKeysFromParams(baseParams);
  }, [baseParams, getQueryKeysFromParams]);

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => {
      // Compute knownTotal at fetch time from the ref so it's always fresh
      // and never affects the query key.
      // Only send knownTotal when navigating to a different page/sort (queryKey
      // changed) but the count-affecting params stayed the same (fingerprint
      // matches). When the queryKey is identical (background refetch / poll),
      // let the backend recompute so totals stay fresh.
      const fp = countFingerprint(filters, search, countScope);
      const currentKeyJson = JSON.stringify(queryKey);
      const knownTotal =
        lastCountRef.current?.fingerprint === fp &&
        lastCountRef.current.queryKeyJson !== currentKeyJson
          ? lastCountRef.current.total
          : undefined;
      const fetchParams: DataFetchParams = { ...baseParams, knownTotal };
      return queryFn(fetchParams, signal);
    },
    placeholderData: previousData => previousData,
    ...queryConfig,
  });

  const responseMeta = query.data?.meta;
  useEffect(() => {
    if (responseMeta !== undefined) {
      onMeta?.(responseMeta);
    }
  }, [responseMeta, onMeta]);

  // Update the cached total whenever a fresh (non-placeholder) response arrives.
  // Skip placeholder data to avoid pairing a new fingerprint with a stale total.
  const paginationTotal = query.data?.pagination?.total;
  const isPlaceholder = query.isPlaceholderData;
  useEffect(() => {
    if (paginationTotal == null || isPlaceholder) return;
    const fp = countFingerprint(filters, search, countScope);
    const keyJson = JSON.stringify(queryKey);
    if (
      !lastCountRef.current ||
      lastCountRef.current.fingerprint !== fp ||
      lastCountRef.current.total !== paginationTotal ||
      lastCountRef.current.queryKeyJson !== keyJson
    ) {
      lastCountRef.current = {
        fingerprint: fp,
        total: paginationTotal,
        queryKeyJson: keyJson,
      };
    }
  }, [paginationTotal, filters, search, countScope, isPlaceholder, queryKey]);

  return {
    data: query.data?.data ?? [],
    pagination: query.data?.pagination ?? DEFAULT_PAGINATION_META,
    meta: query.data?.meta ?? DEFAULT_RESPONSE_META,
    isLoading: query.isPending,
    isShowingPlaceholder: query.isPlaceholderData,
    isQueryFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export interface UseDataTableQueryResult<TRow extends TableRowData> {
  data: TRow[];
  pagination: PaginationResultsMeta;
  meta: ResponseMeta;
  /** True only on initial load (no data yet) — show full skeleton */
  isLoading: boolean;
  /**
   * TanStack's `isPlaceholderData`: the previous page's rows are on screen
   * while a new page, filter or sort loads — show a subtle indicator.
   *
   * Named for what it is rather than `isFetching`, which a reader would take
   * for TanStack's own flag of that name. That one is {@link isQueryFetching}.
   */
  isShowingPlaceholder: boolean;
  /**
   * TanStack's `isFetching`: a request for the active query is in flight,
   * the initial one included. Not TanStack's `isRefetching`, which stays
   * false until the query already has data.
   */
  isQueryFetching: boolean;
  isError: boolean;
  error: unknown;
  refetch: UseQueryResult<unknown>['refetch'];
}
